import content from './content/en';

export default {
  content,
  confirmation: {
    deviceSerial: 'Serial {serial}',
    currentValue: 'Current',
    newValue: 'New',
    manualSource: 'Manual entry',
    deviceUser: 'User {user}',
    deleteRestriction:
      'Delete is available only for readings entered today. At least one of these entries was added on an earlier day.',
    unsavedTitle: 'Discard unsaved changes?',
    unsavedDescription: 'Your changes have not been saved. Closing now will discard them.',
    discard: 'Discard changes',
    keepEditing: 'Keep editing',
    updateTitle: 'Save changes to these readings?',
    updateDescription:
      'Your updated readings will be saved and your fluid monitoring results will be updated.',
    saveChanges: 'Save changes',
  },
  fluidDetails: {
    actions: 'Reading actions',
    saved: 'Saved',
    edit: 'Edit reading',
    editBefore: 'Edit Before dialysis',
    editAfter: 'Edit After dialysis',
    deleteTitle: 'Delete reading?',
    deleteDescription:
      'This will delete the weight and blood pressure readings saved in this entry. Your fluid monitoring results will be updated.',
    deleteSessionTitle: 'Delete dialysis session readings?',
    deleteSessionDescription:
      'This will delete both the Before and After weight and blood pressure readings for this dialysis session. Your fluid monitoring results will be updated.',
    deleteSession: 'Delete dialysis session',
    noDryWeight: 'No prescribed dry weight',
    savedDevice: 'Saved device',
    unpair: 'Disconnect',
  },
  appName: 'AI Manage Dialysis',
  termsPolicy: {
    summaryTitle: 'Summary',
    summary:
      'This app helps you track your health and learn about self-management, but it does not replace your dialysis care team and is not medical advice, so don’t change your dialysis, daily fluid limit, dry weight, diet or medicines based only on the app. In an emergency, go to your dialysis centre or nearest hospital, or call 995, and don’t wait for the app. Your care team does not watch the app all the time, so contact them if you feel unwell or unsure. Your data is protected under Singapore’s PDPA. Tap any section below to read the full details.',
    sections: {
      about: {
        title: 'About this app',
        text: 'This app helps you track your weight, blood pressure, symptoms, diet and mood, and gives you educational resources to support your daily self-management. It is designed for use alongside your regular care, not in place of it.',
      },
      notAdvice: {
        title: 'Not medical advice',
        text: 'The information, alerts, reminders, chatbot replies and other content in this app are for self-management and education only. They are not medical advice, diagnosis or treatment, and they don’t replace the judgement of your doctor, nurse, dietitian or dialysis care team. Do not change your dialysis schedule, fluid limit, dry weight, diet or medication based only on this app. Always follow your healthcare team’s instructions.',
      },
      emergency: {
        title: 'In an emergency',
        text: 'This app is not an emergency service. If you have a medical emergency, such as severe breathlessness, chest pain, fainting, seizure, severe swelling or any sudden worsening of your condition, go to your dialysis centre or the nearest hospital, or call 995 if you need an ambulance. Don’t wait for an app alert, a reply from the care team or a chatbot response.',
      },
      unsure: {
        title: 'If you are unsure, seek medical attention',
        text: 'If you are unsure about any alert, reading, symptom, recommendation or message in this app, contact your healthcare professional at your dialysis centre or hospital. If the app shows no alert, that does not mean you are well. If you feel unwell, please seek medical care.',
      },
      monitoring: {
        title: 'Monitoring is not real time',
        text: 'Alerts, such as fluid weight gain warnings, may be shared with your care team. The care team does not monitor the app continuously, and there is no guarantee of an immediate response. Alerts can also be missed or delayed if your phone is off, offline or has notifications disabled. Do not rely on the app as your only way of being monitored.',
      },
      accuracy: {
        title: 'Accuracy of information you enter',
        text: 'Your results depend on the accuracy of what you enter or sync from connected devices. Please enter readings honestly and carefully. Device readings may be inaccurate or fail to sync, and you should check them or contact the research coordinator if something looks wrong.',
      },
      ai: {
        title: 'AI Chatbot and Diet Analysis',
        text: 'The chatbot and AI diet features are automated, can make mistakes, and only answer within a limited knowledge area. Their outputs, including food photo analysis and weekly diet summaries, are general guidance. Please verify important information with your healthcare team.',
      },
      wellbeing: {
        title: 'Emotional wellbeing',
        text: 'The mood check-in and support suggestions are not a counselling or crisis service. If you feel unable to cope or are thinking about harming yourself, contact emergency services, a crisis helpline or your dialysis centre immediately.',
      },
      reminders: {
        title: 'Reminders',
        text: 'Reminders are only an aid and may not work if your phone settings block notifications. You are responsible for taking your medication as prescribed.',
      },
      privacy: {
        title: 'Your data and privacy',
        items: {
          collect: {
            label: 'What we collect',
            text: 'Weight, blood pressure, symptoms, blood test results you enter, diet photos and diary, mood check-ins, reminders, chat messages and app usage.',
          },
          why: {
            label: 'Why',
            text: 'To provide app features, to help your care team support you, and to improve the service.',
          },
          who: {
            label: 'Who can see it',
            text: 'You, your linked caregiver, authorised NKF staff and NUS research team through the portal.',
          },
          rights: {
            label: 'Your rights',
            text: 'You can request access, correction or deletion of your data and can withdraw consent.',
          },
          safeguards: {
            label: 'Safeguards',
            text: 'Your data will be kept only for the approved ethical board data storage period. After that period, it will be securely disposed of.',
          },
          legal: {
            label: 'Legal basis',
            text: 'We handle your personal data in accordance with Singapore’s Personal Data Protection Act 2012 (PDPA).',
          },
        },
      },
      caregiver: {
        title: 'Caregiver access',
        text: 'A caregiver may use the app in the patient view and record data on the patient’s behalf. By linking a caregiver, the patient agrees that the caregiver can see and enter their health information. Caregivers must act in the patient’s best interests and keep the information confidential.',
      },
      security: {
        title: 'Account security',
        text: 'Keep your login details private and don’t share them with anyone. Tell us immediately if you suspect unauthorised access.',
      },
      liability: {
        title: 'Limitation of liability',
        text: 'To the extent permitted by law, NKF, NUS and its developers are not liable for loss or harm arising from use of the app or reliance on its content, including missed or delayed alerts, inaccurate readings, or errors in AI outputs. This does not exclude liability that cannot be excluded by law.',
      },
      updates: {
        title: 'Updates to these terms',
        text: 'We may update these terms or the app from time to time. Where changes are significant, we will ask you to review and accept them again.',
      },
    },
  },
  consentDetails: {
    review: 'Please review how your information is used before continuing.',
    patient: 'PATIENT {id}',
  },
  scheduleEditor: {
    add: 'Add reminder',
    edit: 'Edit reminder',
    save: 'Save reminder',
    update: 'Update reminder',
    remove: 'Remove reminder',
    addReading: 'Add reminder time',
    times: 'Reminder times',
    moreTimes: 'Add as many as you need',
    medicinePlaceholder: 'e.g. Sevelamer',
    dosePlaceholder: 'e.g. 800 mg',
    custom: 'Custom',
    what: 'What to remind',
    advice:
      'If a reading isn’t logged by the reminder time, we send one notification. If it’s still missing 2 hours later, we send one follow-up, then pause until the next scheduled time.',
    saved: 'Reminder saved',
    updated: 'Reminder updated',
    removed: 'Reminder removed',
    keepOneTime: 'Keep at least one time',
    selectDay: 'Please choose at least one day.',
    nameRequired: 'Please enter a medication name.',
    photo: 'Photo',
    optional: 'Optional',
    medicineOn: '{name} reminder on',
    medicineOff: '{name} reminder off',
    reminderOn: '{name} on',
    reminderOff: '{name} off',
    names: {
      weight: { name: 'weight reminder', description: 'Log weight' },
      bp: { name: 'blood pressure reminder', description: 'Log blood pressure' },
      both: { name: 'weight and BP reminder', description: 'Log weight and BP' },
    },
  },
  readingValidation: {
    weight: 'Enter a weight between 0.1 and 500 kg.',
    systolic: 'Systolic BP should be between 50 and 300.',
    diastolic: 'Diastolic BP should be between 30 and 200 and lower than systolic.',
    heartRate: 'Heart rate should be a whole number between 20 and 250 bpm.',
    beforeDialysis: 'Before dialysis: {message}',
    afterDialysis: 'After dialysis: {message}',
    afterIncomplete: 'After dialysis: enter your weight or complete blood pressure and heart rate.',
  },
  promptDetails: {
    dismiss: 'Dismiss',
    gapSummary: '{days} days without a recorded session',
    scheduled: 'Scheduled for {time}',
    attendanceRecorded: 'Attendance recorded — thank you',
    attendanceNoted: 'Noted. Please contact your care team if you missed your session.',
    attendanceDaysRecorded:
      'Attendance recorded for {count} day | Attendance recorded for {count} days',
    medicationTaken: 'Marked as taken',
    medicationSnoozed: 'Reminder snoozed for {count} minute | Reminder snoozed for {count} minutes',
    medicationSkipped: 'Dose skipped',
    readingDescription: 'Scheduled for {time}.\nPlease complete your {measurement} check-in.',
    readingFollowupDescription:
      'Your {time} {measurement} check-in is still incomplete.\nIt’s now {later}.',
    recordReading: 'Record my weight & BP',
    dryTitle: 'Dry weight updated',
    since: 'since {date}',
    fromToday: 'from today',
    attendanceDay: 'Today is {day}, one of your scheduled dialysis days.',
    gapTitle: 'It looks like you haven’t attended dialysis recently',
    gapAlertDescription:
      'Your weight gain since your last dialysis session is above your care team’s 4% threshold',
    gapAlertInfo:
      'Your records do not show a dialysis session in the past {days} days. If you have already attended dialysis, please let your care team know.',
    idwgAlertInfo:
      'IDWG shows how much weight you have gained between dialysis sessions, compared with your dry weight. The colour bands below help you understand your result.',
    gapDescription: 'We don’t have dialysis-day readings for the past {days} days.',
    went: 'I went',
    notWent: 'I haven’t gone',
    selectDays: 'Select all the days you went.',
    missingRisk: 'Missing dialysis can be risky',
    reasonPlaceholder: 'e.g. I was unwell, no transport, or the centre was closed',
    attendanceAdvice: 'Attending dialysis as scheduled is important to keep you safe and healthy.',
    attendanceRisk:
      'Skipping dialysis sessions can cause fluid and waste to build up in your body. Please contact your care team as soon as you can.',
    callCareTeam: 'Call care team',
    tier: 'Tier',
    idwg: 'Your weight gain since your last dialysis session is {percent} of your dry weight — above your care team’s {threshold} threshold.',
    idwgAdviceTitle: 'What to do now:',
    tierName: 'Tier {code}',
    tierAria: '{name}, {color}',
    tierColor: { green: 'Green', yellow: 'Yellow', orange: 'Orange', red: 'Red' },
    tierRange: {
      maximum: '< {to}% of dry weight',
      minimum: '≥ {from}% of dry weight',
      between: '{from} to <{to}% of dry weight',
    },
    dryCardUpdated: 'Your nurse updated your dry weight to {weight}.',
    dryCardView: 'View update',
    dryCardTitle: 'Dry weight {weight} kg',
    dryCardCurrent: 'Your current dry weight.',
    dryPillSaved: 'Saved',
    dryPillConfirmed: 'Confirmed',
    dryUpdated: 'Your nurse has updated your target dry weight.',
    previous: 'Previous',
    new: 'New',
    medicine: 'Time for your medicine',
    reading: 'Time to record {measurement}',
    readingFollowup: 'Your {measurement} check-in is incomplete',
    sameTime: 'Recording readings as scheduled helps your care team spot changes early.',
    lastReminder: 'This is your last reminder for this time slot.',
  },
  notificationDetails: {
    alreadyAnswered: 'Already completed. The saved response was kept.',
    idwgAlert: 'IDWG {percent}% — at or above the alert threshold',
  },
  supportDetails: {
    emergencyContact: 'Emergency contact',
    emergencyHint: 'If you feel seriously unwell, call now.',
    emergencyAdvice: 'If it’s an emergency, call emergency services now.',
    emergencyCall: 'Emergency call · 995',
    officeHours: 'Office hours',
    whenCall: 'When to call',
    callAdvice:
      'For life-threatening symptoms, call emergency services. For non-urgent questions, contact your care team during office hours.',
    careTeam: 'Your care team',
    noCentre: 'No dialysis centre is connected to your account.',
    noTelephone: 'Phone number is not available.',
    notAlone: 'You’re not alone',
    journey: 'Get the support you need on this journey.',
    emotionalHealth: 'Check-in history, resources and support',
    social: 'Peer support, advocacy and local services',
    careContacts: 'Contact your dialysis centre',
    community: 'Support and community resources for you and your family.',
  },
  historyDetails: {
    average: 'Average',
    highest: 'Highest',
    lowest: 'Lowest',
    systolicLegend: 'systolic',
    diastolicLegend: 'diastolic',
    avgSystolic: 'Avg systolic',
    avgDiastolic: 'Avg diastolic',
    avgHeartRate: 'Avg heart rate',
    bpShort: 'BP',
    hrShort: 'HR',
    pre: 'pre',
    post: 'post',
    regular: 'Regular',
    recordedCheckins: 'Recorded check-ins',
    records: '{count} record | {count} records',
    symptom: 'Symptom',
    severity: 'Severity',
    createdBy: 'Created By',
    noRecords: 'No records.',
  },
  mealEntry: {
    dailyLimit:
      'You have reached the limit of {limit} meal submissions for this date. Deleted meals still count.',
    analyzing: 'Analyzing and saving…',
    dateTooOld: 'You can add meals for today and the previous two days only.',
    photoTooLarge: 'This photo is too large. Maximum size is 2 MB.',
    todayMeals: 'Today’s meals',
    firstMeal: 'Log your first meal',
    goodStart: 'You’re off to a good start',
    progressing: 'You’re progressing well',
    addPhoto: 'Add photo',
    photoHint: 'Take a photo or choose from your gallery',
    samplePhoto: 'Use a sample photo',
    timeEaten: 'Time eaten',
    add: 'Add this meal',
    editTime: 'Edit time',
    guidance:
      'Save up to 3 meals per day, including deleted meals. To log a past meal, pick a date in History (up to 2 days back).',
    limit: 'This date has reached 3 meal submissions. Deleted meals still count.',
    yourMeal: 'Your meal',
    fromPhoto: 'Added from your photo.',
    loggedCount: '{count} logged',
    emptyToday: 'No meals yet — add one',
    emptyDay: 'No meals logged on this day.',
    changeTime: 'Change time',
    saveTime: 'Save time',
    pickTime: 'Pick a time',
    timeUpdated: 'Meal time updated',
    deleted: 'Meal deleted',
    added: 'Food added',
    nowTime: 'Now · {time}',
  },
  pickers: {
    date: 'Select date',
    time: 'Select time',
    selectedDate: 'Selected date',
    selectedTime: 'Selected time',
    pickDate: 'Pick a date',
    setTime: 'Set time',
    confirm: 'Confirm',
    yesterday: 'Yesterday',
    previousYear: 'Previous year',
    nextYear: 'Next year',
    hour: 'Hour',
    minute: 'Minute',
    period: 'AM / PM',
    am: 'AM',
    pm: 'PM',
  },
  moodDetails: {
    trendTitle: 'Your check-ins over the past 7 days',
    missingDays: 'Days without a check-in have no score.',
    noCheckin: 'No check-in',
    empty: 'No check-ins in the past 7 days',
    unavailable: 'This check-in is unavailable',
    thankYou: 'Thank you',
    saved: 'Mood saved: {mood}',
    nurse: 'Your care nurse',
    wellbeing: 'Your emotional wellbeing',
    history: 'Check-in history',
    pastWeek: 'Past 7 days',
    yourNotes: 'Your notes',
    selectedFeeling: 'Selected feeling',
    suggestedSupport: 'Suggested support',
    severalDays:
      'If you feel low for several days, please talk to your care team or someone you trust.',
    supportText:
      'Keep up activities that help you feel good. Reach out to your care team if you feel low for several days.',
    lowAdvice:
      'It’s okay to have low days. Consider speaking with your care team or someone you trust.',
    okayAdvice: 'A neutral day is fine. Try a short walk or a call with a friend.',
    goodAdvice: 'It’s great to see you feeling good. Keep doing what’s working well for you.',
  },
  dailyGoals: {
    title: 'Your progress today',
    intro: 'Complete your daily check-ins to stay on track.',
    tasksCompleted: '{done} of {total} tasks completed',
    completed: '{done} of {total} completed',
    percentComplete: '{percent}% complete',
    backHome: 'Back to Home',
    incomplete: 'Not completed',
    feedbackStart: 'Let’s get started — your first task is waiting.',
    feedbackProgress: {
      firstTask: [
        'Good start!',
        'Well done for starting!',
        'First task done. Good job!',
        'You’ve taken the first step. Keep it up!',
        'Good effort! Every task helps you stay on track.',
        'That’s one task done!',
        'You’ve begun today’s tasks. Well done!',
        'One task done! We’ll remind you about the next one.',
        'Thanks for doing this! Keep going.',
        'Great, that’s the first task!',
      ],
      over20: [
        'You’re doing well!',
        'Good progress so far!',
        'You’re on track. Keep it up!',
        'Good job! You’re building a good routine.',
        'You’re making steady progress today.',
      ],
      over40: [
        'You’re doing great!',
        'You’re making good progress!',
        'Nice and steady!',
        'Great work so far!',
        'You’re doing well! Carry on when the next task is ready.',
        'Thanks for staying on track today!',
      ],
      over50: [
        'More than halfway done!',
        'You’re almost there. Great work!',
        'You’ve passed the halfway mark!',
        'Well done! Keep it going.',
        'Good progress today!',
        'Excellent work so far!',
      ],
      over70: [
        'Good effort today!',
        'Thank you for looking after yourself today!',
        'Keep up the good work!',
      ],
      over85: [
        'You’ve nearly completed today’s tasks',
        'Almost done for today.',
        'Well done!',
        'Nearly there. Good job!',
      ],
    },
    feedbackComplete: [
      'All tasks complete. Well done!',
      'You’ve completed everything today. Great job!',
      'That’s everything for today. Rest well!',
      'Thank you for taking care of your health today!',
      'Great job today!',
      'Every task is complete. Thank you!',
      'You’ve completed today’s tasks. Take a good rest!',
      'Today’s tasks are complete. Keep up the good habit!',
      'Excellent effort today. See you tomorrow!',
      'Well done! You’ve taken good care of yourself today.',
    ],
  },
  goalDetails: {
    title: 'Goals',
    empty: 'No goals configured',
    articles: '{done}/{total} articles',
    count: '{count} goals',
    days: '{done}/{total} days',
    checkins: '{done} of {total} check-ins done',
    complete: 'complete',
    thisWeek: 'This week',
  },
  callSheet: {
    nurseLine: 'Nurse Line',
    emergencyServices: 'Emergency services',
    loginSupport: 'Login support',
    loginSupportIntro:
      'The current trial version is available only to eligible patients. If you are a registered NKF dialysis patient and cannot log in, please call the number below.',
    hint: 'Tap “Call now” to open your phone dialler.',
    callNow: 'Call now',
  },
  profileDetails: {
    caregiverAccess: 'Caregiver access',
    watching: 'Watching {patient} profile',
    centre: 'Your dialysis centre',
    schedule: 'Your dialysis schedule',
    appLanguage: 'App language',
    passwordHint: 'Update your login password',
    notConfigured: 'Not set',
  },
  contactHelp: {
    search: 'Search FAQs...',
    contact: 'Contact us',
    title: 'How can we help?',
    intro: 'Find quick answers about using the app and your health information.',
    emergency: 'Need immediate help?',
    noResults: 'No questions found.',
  },
  articleActions: {
    categories: 'Article categories',
    search: 'Search articles...',
    title: 'Article',
    noResults: 'No articles found.',
  },
  devices: {
    intro:
      'Pair a Bluetooth blood-pressure monitor or scale, then use Sync in an entry to import saved readings.',
    slotUnavailable:
      'This device has no free user slot for you. Free a slot on the device, then scan again.',
    noneFound: 'No device found. Keep your device close and in pairing mode, then scan again.',
    scanning: 'Scanning for nearby devices…',
    connected: 'Connected',
    syncedJustNow: 'synced just now',
    syncedAt: 'synced {time}',
    measuredAt: 'Measured {datetime}',
    readyToSync: 'Ready to sync',
    disconnected: 'Disconnected',
    nearby: 'Available nearby',
    pair: 'Pair',
    find: 'Scan for devices',
    typeBp: 'Blood pressure monitor',
    typeScale: 'Smart scale',
    kindBp: 'blood pressure monitor',
    kindScale: 'scale',
    kindAll: 'device',
    unsupported: 'Pairing devices needs the installed app on your phone.',
    unavailable: 'Devices could not be prepared. Leave this screen and open it again to retry.',
    unavailableWithCode:
      'Device connection could not start (code {code}). Close and reopen this screen to retry. If this continues, contact support with this code.',
    bluetoothOff: 'Bluetooth is off. Turn it on in your phone’s Settings, then return to the app.',
    permissionNeeded: 'Allow Bluetooth access so the app can find your devices.',
    allowAccess: 'Allow access',
    openSettings: 'Open settings',
    deviceAction: 'Press the pairing button on your device.',
    connecting: 'Connecting to {names}…',
    connectingDevice: 'Connecting to your device…',
    pairedToast: 'Device paired',
    pairFailed: 'The device could not be paired (code {code}). Try again.',
    removeFailed: 'The device could not be disconnected (code {code}). Try again.',
    accessFailed: 'Bluetooth access could not be updated (code {code}). Try again.',
    noLinked: 'No paired {kind}. Open Manage devices to pair one, then try again.',
    syncLoading: 'Reading saved device readings…',
    syncFailed: 'The readings could not be loaded. Your entry has not changed. Try Sync again.',
    noNewReading:
      'No new readings from your {kind} are saved on this phone. Transfer the results to this phone, then try Sync again.',
    readingsReceived: 'Device readings received',
    syncedPartial: 'Your {kind} has no new reading to add. The received readings are shown below.',
    uploadFailed: 'Your entry was saved, but the device reading could not be uploaded.',
  },
  assistant: {
    title: 'NKF Assistant',
    status: 'Assistant preview',
    greeting:
      'Hi {name}! I’m your NKF assistant. I can see you’re on the **{page}** page — ask me anything about using the app, your fluid and diet routine, or where to find things.',
    nowOn: 'You’re now on the **{page}** page. Need a hand with anything here?',
    placeholder: 'Ask me anything…',
    disclaimer: 'Sample replies only. No AI service is connected. This is not medical advice.',
    typing: 'The assistant is typing',
    send: 'Send',
    suggestions: {
      idwg: 'What is IDWG?',
      dryWeightChange: 'Why does dry weight change?',
      callCareTeam: 'Call my care team',
      logMeal: 'How do I log a meal?',
      potassiumFoods: 'Which foods are high in potassium?',
      weeklySummary: 'What is a weekly summary?',
      whenCallCareTeam: 'When should I call my care team?',
      logSymptom: 'How do I log a symptom?',
    },
    actions: {
      openFluid: 'Open Fluid',
      viewDryWeight: 'View dry weight update',
      openFoodGuide: 'Open Food Guide',
      bloodResults: 'Blood results',
      logWeightBp: 'Log weight & BP',
      logMeal: 'Log a meal',
      openDiary: 'Open Diet Diary',
      medicationReminders: 'Medication reminders',
      goals: 'Goals',
      openSymptoms: 'Open Symptoms',
      openProfile: 'Open Profile',
      switchLanguage: 'Switch language',
      callSupport: 'Call support',
      callCareTeam: 'Call care team',
      call995: 'Call 995',
      seeContacts: 'See all contacts',
      openResults: 'Open results',
      openFaq: 'Open FAQ',
      weeklySummary: 'Weekly summary',
    },
    replies: {
      emergency:
        '**Please seek urgent help.** If you have severe difficulty breathing, chest pain or feel faint, call emergency services (995) right away. Do not wait for an app reply.',
      hello: 'Hello! How can I help you today?',
      thanks: 'You’re welcome! I’m here whenever you need me.',
      idwg: '**IDWG** is your weight change between dialysis sessions, shown as a % of your dry weight. Open **Fluid** to see your current reading and your care team’s configured alert guidance.',
      dryWeight:
        'Your **dry weight** is the weight your care team targets after dialysis, when extra fluid has been removed. You can review their updates and enter your dry weight in **Fluid**.',
      potassium:
        'High-potassium foods include bananas, oranges, tomatoes and potatoes; phosphorus is high in dairy, nuts and cola. The **Food Guide** lists kidney-friendly choices and portion tips.',
      fluid:
        'Sip slowly from a small cup, suck on ice chips or sugar-free sweets, and avoid salty foods — salt makes you thirstier. Log your weight daily so we can spot gains early.',
      meal: 'Open **Diet Diary** or tap “Log a meal” on Home, add a photo and press **Add this meal**. The AI estimates nutrition and gives a tip. You can save up to 3 meals per day, including deleted meals.',
      bloodPressure:
        'Take your blood pressure seated and rested, at the same time each day. Use **At Home** or **At Dialysis Centre** on the Fluid page. If you have paired a monitor, press **Sync** in the entry to use its readings.',
      bloodTest:
        'Your latest blood results are on **Diet → Blood Test Results**. Each value shows where it sits in the usual range, with a short recommendation. You can also add a new result or view the trend.',
      medication:
        'Add reminders under **More → Medication Reminders**. If you miss a dose, don’t double up — take the next one as scheduled and tell your care team if it happens often.',
      goals:
        'Track your weekly goals — logging weight, BP and meals, and reading articles — in **More → Goals**.',
      symptom:
        'Log how you feel in the **Symptoms** tab. Tell your care team if a symptom is new, getting worse, or lasts more than a day. For breathing difficulty or chest pain, call 995.',
      schedule:
        'Your dialysis schedule is shown in **Profile**. The app checks attendance on those days and uses your care team’s configured gap threshold for follow-up.',
      language:
        'You can switch between English and 中文 using the language button at the top of the Login page, or in your Profile.',
      login:
        'If you can’t log in, tap **Need help logging in?** on the Login page to call the support line.',
      contact:
        'Your selected dialysis centre’s details are shown under **Support → Care Team Contacts**.',
      approximate:
        'Nutrition values are AI estimates from your photo and a typical portion. They’re for general reference only and don’t replace advice from your care team or dietitian.',
      weekly:
        'From Monday at 08:00 in your timezone, you can generate a **Weekly AI Summary** of last week’s saved meals, with adherence, a short summary and food tips for the week ahead.',
      fallback:
        'I can help with using the app — logging fluid and meals, reminders and where to find things. For medical questions, please contact your care team.',
    },
  },
  readingStatus: { normal: 'Normal', watch: 'Watch', high: 'High' },
  readingGroups: {
    dialysisInstruction: 'Record your weight and blood pressure before and after your session.',
    afterWord: 'after',
    regularReading: 'Regular reading',
    required: 'Required',
    afterInstruction: 'Add when your session ends',
    pressurePulse: 'Blood pressure & heart rate',
    below: 'Below',
    onTarget: 'On target',
    alert: 'Alert',
  },
  photos: {
    medicine: 'Medication photo',
    preparing: 'Preparing photo',
    replace: 'Change photo',
    remove: 'Remove photo',
  },
  homeCards: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
    daily: 'Daily',
    notLogged: 'Today’s reading not logged yet',
    loggedAt: 'Last reading today at {time}',
    dialysis: 'Dialysis',
    meals: 'meal | meals',
    moodDescription: 'This takes 10 seconds and helps your care team support you better.',
    moodNote: 'Add a note (optional)',
    feeling: 'How are you feeling today?',
    nurseUpdate: 'Dry weight updated by your nurse',
    target: 'Your target is now {weight} kg. Tap to view.',
    new: 'New',
    goals: 'Your goals',
    progress: 'This week · {done} of {total} check-ins done',
    recommended: 'Self-care tips for you',
  },
  assessment: {
    savedTitle: 'Saved',
    saved: 'Your symptom check-in has been saved.',
    trend: 'Your {symptom} Trend',
    feedback: 'Feedback for your check-in just now.',
    severity: 'Your severity level',
    notFelt:
      'You are not feeling this symptom right now. Keep checking in so your care team can see how you are doing.',
    tips: '{symptom} Coping Tips',
  },
  symptomsPage: {
    severeTitle: 'Severe symptoms?',
    severeText: 'Chest pain or trouble breathing — call emergency now.',
  },
  followup: {
    title: 'Are you experiencing any symptoms, e.g. swelling, dizziness or difficulty breathing?',
    question: 'Tell us how you feel so your care team can support you.',
    check: 'Check my symptoms',
    submit: 'Submit',
  },
  mealDetails: {
    types: { breakfast: 'Breakfast', lunch: 'Lunch', snack: 'Snack', dinner: 'Dinner' },
    estimate: 'Nutrition estimate',
    perMeal: 'Per meal · approx.',
    foodAnalysis: 'AI food analysis',
    previewTitle: 'AI information',
    preview:
      'This analysis is generated by AI from your photo, for general reference only. It is not medical advice and does not replace guidance from your care team or dietitian.',
    search: 'Search foods...',
    noFoods: 'No foods found.',
    serving: 'Per typical serving',
    portionAdvice: 'General guidance only. Ask your dietitian about portions that suit you.',
    title: 'Food details',
    watch: 'Things to watch',
    positive: 'Positive point',
    disclaimer:
      'Amounts are approximate estimates for this meal, not personal dietary targets. A dash means an estimate is unavailable.',
    unavailable: 'No saved AI analysis is available for this meal.',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  },
  dryWeightRange: 'Enter a weight between 0.1 and 500 kg.',
  nameLength: 'Use no more than 120 characters.',
  doseLength: 'Use no more than 80 characters.',
  noteLength: 'Use no more than 1,000 characters.',
  microEducation: {
    wonderful: 'Wonderful!',
  },
  symptomLibrary: {
    title: 'Symptoms Library',
    notFelt: 'Not Felt',
    grade: 'Grade {grade}',
    submit: 'Submit',
    closed: 'Symptom closed',
    warning: 'Severe symptoms need attention. Please contact your care team now.',
  },
  weeklyReport: {
    mealsIncluded: 'Meals included in this report',
    coverage:
      'Based on {count} logged meals across {days} of 7 days. Unlogged meals are not assessed.',
    emptyWeek: 'No meals were logged for last week.',
    emptyHistory: 'No weekly summaries yet',
    pending: 'Last week contains meals without AI analysis. A summary cannot be generated yet.',
    mealsLoggedDaily: 'Meals logged each day',
    title: 'Weekly AI Summary',
    review: 'Your weekly diet diary review',
    readyTitle: 'Your last week’s report is ready to generate',
    availableFrom: 'Available from {time}, in your timezone.',
    readyDescription: 'Get a personalised summary based on your diet diary from {from} to {to}.',
    period: 'Report period',
    generate: 'Generate weekly summary',
    confirmTitle: 'Generate weekly summary?',
    confirmDescription:
      'Once the report is generated, you will no longer be able to add or edit food diary records for the previous week ({from} to {to}).',
    continue: 'Continue',
    previous: 'Previous summaries',
    ready: 'Ready to generate',
    readReport: 'Adherence {percent}% · tap to read',
    adherence: 'Diary adherence',
    mealsTarget: 'Meals logged per day (target {target})',
    feedback: 'Feedback',
    dietSummary: 'Summary of your diet',
    favour: 'Foods to favour next week',
    aiTitle: 'AI information',
    disclaimer:
      'This summary is generated by AI from your diet diary for general reference only. It has no medical value and does not replace advice from your care team.',
    generating: 'Generating your weekly summary…',
    locked: 'This period has a weekly summary. Its food diary entries are read-only.',
  },
  visual: {
    lastResults: 'last {count} result | last {count} results',
    latestInRange: 'Your latest result is within the target range.',
    submissions: '{count} submission | {count} submissions',
    status: 'Status',
    byMedication: 'By medication',
    groupByTime: 'Group by time',
    medicationCount: '{count} medication | {count} medications',
    labReportInstruction: 'Enter the values from your lab report.',
    testDate: 'Test date',
    parameter: 'Parameter',
    result: 'Result',
    unit: 'Unit',
    yourSymptoms: 'Your Symptoms',
    symptomTrends: 'Symptom Trends',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    inRange: 'In range',
    high: 'High',
    low: 'Low',
    latestResult: 'Latest result',
    rangeCount: '{count} of {total} in range',
    lookingGood: 'Looking good',
    needsLook: 'Needs a look',
    recommendation: 'Recommendation',
    viewHistory: 'View history',
    series: {
      swell: 'Swelling',
      dizzy: 'Dizziness',
      breath: 'Breathing',
      fatigue: 'Fatigue',
      itch: 'Itching',
      nausea: 'Nausea',
      cramp: 'Cramps',
      sleep: 'Sleep',
    },
  },
  setup: {
    dialysisTime: 'Dialysis time',
    support: 'This helps us provide more personalised support.',
    dryHelp: 'Helps us track your fluid status.',
    scheduleHelp: 'Helps us send timely reminders and relevant guidance.',
    dryInstruction: 'Enter the dry weight you know now and the date you measured it.',
    measured: 'Date measured',
    scheduleQuestion: 'Do you know your dialysis schedule?',
    scheduleInstruction: 'Select the days you usually have dialysis.',
    title: 'Profile setup',
    terms: 'Terms and conditions',
    privacy: 'Your privacy',
    consentNotice:
      'By continuing, you confirm that you have read and agree to the Terms of Use and Privacy Policy.',
    requiredSchedule: 'Select at least one dialysis day and a time to complete setup.',
    completed: 'Setup complete. Your care team has been notified of your dry weight.',
    decline: 'Decline',
    agree: 'I agree & continue',
    basicInformation: 'Let’s set up your basic information',
    schedule: 'Dialysis schedule',
    dryQuestion: 'Do you know your current dry weight?',
    step: 'Step {step} of 3',
    decrease: 'Decrease dry weight',
    increase: 'Increase dry weight',
    actions: { 1: 'Get started', 2: 'Next', 3: 'Finish' },
  },
  interactions: {
    symptomTrends: 'Symptom trends',
    symptomsToExpect: 'Symptoms to expect',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    assessment: 'Assessment',
    supportiveGuidance: 'Supportive guidance',
    now: 'What you can do now',
    why: 'Why this matters',
    reach: 'Reach out if',
    patient: 'Patient',
    caregiver: 'Caregiver',
    all: 'All',
    validTimes: 'Choose a valid time.',
    selectDay: 'Select at least one day.',
    days: 'Days',
    addTime: 'Add another time',
    idwgAlert: 'IDWG alert',
    fullAlertInfo: 'Why am I seeing this alert?',
    medicationReminder: 'Medication reminder',
    skipDose: 'Skip this dose',
    tookDose: 'I took it',
    snooze: 'Remind me in {count} minute | Remind me in {count} minutes',
    dryWeightUpdate: 'Dry weight update',
    newDryWeight: 'New dry weight',
    measuredDate: 'Measured date',
    dryWeightIntro: 'Enter your new dry weight. Your nurse will be notified of the update.',
    dryWeightNote: 'Anything your nurse should know?',
    dryWeightPlaceholder: 'e.g. 53.0',
    changeDryWeightTitle: 'Change Dry Weight',
    optionalNote: 'Note (optional)',
    changeDryWeight: 'Change dry weight',
    attendance: 'Dialysis attendance',
    attendanceQuestion: 'Did you attend dialysis today?',
    attendanceDates: 'Which days did you attend dialysis?',
    attendanceGap: 'Long gap since dialysis',
    attendanceReason: 'Could you tell us the reason?',
    readingReminder: 'Weight & BP reminder',
    readingFollowup: 'Weight & BP follow-up',
    yes: 'Yes',
    no: 'No',
    bloodTrends: 'Blood test trends',
    allResults: 'All results',
    viewTrend: 'View trend',
    futureDate: 'Choose today or an earlier date.',
    social: 'Social',
    openLinks: 'Open links',
    emotionalHealth: 'Emotional Health',
    careContacts: 'Care Team Contacts',
    connectBelong: 'Connect & belong',
    checkinDetail: 'Check-in detail',
    noNote: 'No note added.',
    call: 'Call now',
  },
  tracked: 'tracked',
  mealsToday: 'meals today',
  recordWeight: 'Record your weight',
  recordBP: 'Record your blood pressure',
  dietTitle: 'Diet & Nutrition',
  diaryIntro: 'Photo-log your meals',
  foodGuideIntro: 'Kidney-friendly food tips',
  weeklyIntro: 'Your week in review',
  bloodIntro: 'Gauges & trends',
  todayDiary: 'Today’s diary',
  mealsLogged: '{count} meal logged | {count} meals logged',
  moreIntro: 'Personalised content, reminders and support.',
  faqIntro: 'Quick answers',
  medicationIntro: 'Never miss a dose',
  reminderIntro: 'Control your health',
  goalsIntro: 'Track your weekly goals',
  supportIntro: 'Care team & wellbeing',
  target: 'Target',
  graph: 'Graph',
  table: 'Table',
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  fluidHistory: 'Fluid history',
  navigation: 'Main navigation',
  home: 'Home',
  fluid: 'Fluid',
  symptoms: 'Symptoms',
  diet: 'Diet',
  more: 'More',
  profile: 'Profile',
  back: 'Back',
  close: 'Close',
  save: 'Save',
  saveEntry: 'Save entry',
  cancel: 'Cancel',
  confirm: 'Confirm',
  delete: 'Delete',
  add: 'Add',
  done: 'Done',
  search: 'Search',
  all: 'All',
  today: 'Today',
  history: 'History',
  viewAll: 'See all',
  open: 'Open',
  saved: 'Saved in this preview',
  mealSubtitle: 'Take a photo of what you ate',
  logMeal: 'Log a meal',
  quickActions: 'Quick actions',
  weight: 'Weight',
  bp: 'Blood pressure',
  heartRate: 'Heart rate',
  dryWeight: 'Dry weight',
  idwg: 'IDWG',
  normal: 'Normal',
  fluidIntro: 'Track weight, blood pressure and fluid trends.',
  todayStatus: 'Today status',
  viewHistory: 'View history',
  dryShort: 'Dry',
  deviceConnected: 'Device connected',
  todaySubtitle: 'Today · {time}',
  todaySubtitlePart: 'Today · {time} · {part}',
  lastReading: 'Last reading · {date}',
  lastRecordToday: 'Last record: Today, {time}',
  lastRecordOn: 'Last record: {date}, {time}',
  noEntryYet: 'No entry yet',
  dialysisCompleted: 'Completed',
  dialysisBeforeSaved: 'Before saved · add After',
  idwgPercent: '{percent}% IDWG',
  latestWeight: 'Latest weight',
  entrySaved: 'Entry saved',
  saveDialysisDay: 'Save dialysis day',
  updateDialysisDay: 'Update dialysis day',
  dialysisDaySaved: 'Dialysis day saved',
  dialysisDayUpdated: 'Dialysis day updated',
  syncing: 'Sync…',
  manageDevices: 'Manage devices',
  enterWeightBP: 'Enter weight & blood pressure',
  atHome: 'At Home',
  atDialysis: 'At Dialysis Centre',
  weightTrend: 'Weight vs dry weight',
  weightBPEntry: 'Weight & BP entry',
  dialysisEntry: 'Dialysis day record',
  beforeDialysis: 'Before dialysis',
  afterDialysis: 'After dialysis',
  systolic: 'Systolic BP',
  diastolic: 'Diastolic BP',
  date: 'Date',
  time: 'Time',
  notes: 'Notes',
  sync: 'Sync',
  connectDevice: 'Connect a device',
  noReadings: 'No readings yet',
  noEntries: 'No entries yet',
  severity: 'Severity',
  none: 'None',
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
  symptomHistory: 'Symptom history',
  mood: 'How are you feeling?',
  moodHistory: 'Mood history',
  moodLabels: ['Very low', 'Low', 'Okay', 'Good', 'Great'],
  dietIntro: 'Tools to help you make better food choices.',
  diary: 'Diet Diary',
  foodGuide: 'Food Guide',
  weekly: 'Weekly AI Summary',
  mealPhoto: 'Meal photo',
  description: 'Description',
  unsupportedPhoto: 'Choose a JPEG, PNG or WebP photo.',
  photoError: 'This photo could not be opened. Please choose another.',
  calories: 'Calories',
  protein: 'Protein',
  sodium: 'Sodium',
  potassium: 'Potassium',
  phosphorus: 'Phosphorus',
  fluidAmount: 'Fluid',
  education: 'Micro-education',
  educationIntro: 'Short articles & tips',
  readArticle: 'Read article',
  markRead: 'Mark as read',
  goals: 'Goals',
  weeklyGoals: 'Your goals',
  medications: 'Medication Reminders',
  reminders: 'Weight & BP Reminders',
  medicineName: 'Medication name',
  dose: 'Dose',
  schedule: 'Schedule',
  everyDay: 'Every day',
  both: 'Weight + BP',
  bloodResults: 'Blood Test Results',
  faq: 'FAQ',
  support: 'Support',
  social: 'Social support',
  language: 'Language',
  logout: 'Log out',
  logoutConfirm: 'Log out of your account?',
  logoutDescription: 'Your next visit will require your username and password.',
  previewNote: 'Preview entries are not sent to your care team.',
  ai: 'Ask a question',
  notifications: 'Notifications',
  required: 'This field is required.',
  dateRequired: 'Choose a valid date and time.',
};
